import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, LogOut, RefreshCw } from 'lucide-react';
import { useOrgStore } from '../../store/useOrgStore';
import { useAuthStatus } from '../../hooks/useAuthStatus';
import { recordBaloClick, useBaloUrl } from '../../lib/hubApi';

// Score bands map to the existing pillar palette via the semantic tokens.
// 80+ healthy → grow (success). 50–79 attention → sunrise (warning).
// <50 critical → sunset (danger).
function scoreBand(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'danger';
}

export function HubTopBar() {
  const navigate = useNavigate();
  const orgName = useOrgStore((s) => s.orgName);
  const { status } = useAuthStatus();
  const orgType = status?.orgType ?? null;
  const healthscore = useOrgStore((s) => s.audit.healthscore);
  const reset = useOrgStore((s) => s.reset);
  const score = healthscore?.total ?? null;
  const baloUrl = useBaloUrl();

  // Re-run bounces to /connecting, which runs every audit step in parallel
  // and comes back to /hub when it's done. Keeps the spinner UX in one place
  // and avoids threading rerun state through HubLayout. Since TYR-216 the Hub
  // is the only surface that runs a live-session audit.
  const handleRerun = () => navigate('/connecting');

  const handleDisconnect = async () => {
    try {
      await axios.delete('/api/auth/logout', { withCredentials: true });
    } catch {
      // Best-effort: still clear client state.
    }
    reset();
    toast.success('Disconnected');
    // Route to the client-facing /hub-login (not the consultant /connect)
    // so disconnected client admins see OrgIQ Hub branding, not the
    // internal "connect your client's org" framing.
    navigate('/hub-login', { replace: true });
  };

  return (
    <header className="h-16 border-b border-border-subtle bg-surface-0 px-6 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">
          Connected org
        </div>
        <div className="text-[15px] font-semibold text-text-strong truncate flex items-center gap-2">
          {orgName ?? 'Loading…'}
          {orgType ? (
            <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-2 text-text-muted">
              {orgType}
            </span>
          ) : null}
        </div>
      </div>
      {score !== null && <ScoreBadge score={score} />}
      {baloUrl ? (
        <a
          href={baloUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => recordBaloClick('/api/hub/balo-click')}
          title="Book a call with Cyril Duvignau on Balo"
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-sfblue text-white font-semibold hover:opacity-90 transition-opacity"
        >
          <CalendarClock size={13} />
          Book a call
        </a>
      ) : null}
      <button
        type="button"
        onClick={handleRerun}
        title="Re-run the full audit against the connected org"
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border bg-surface-1 text-text hover:bg-surface-hover hover:border-border-strong transition-colors"
      >
        <RefreshCw size={13} />
        Re-run audit
      </button>
      <button
        type="button"
        onClick={handleDisconnect}
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border bg-surface-1 text-text hover:bg-surface-hover hover:border-border-strong transition-colors"
      >
        <LogOut size={13} />
        Disconnect
      </button>
    </header>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const band = scoreBand(score);
  // Use a tinted background + colored numeric for the band cue. The label
  // stays neutral so the eye lands on the number.
  const tint = {
    success: 'bg-success/10 border-success/30 text-success',
    warning: 'bg-warning/10 border-warning/30 text-warning',
    danger: 'bg-danger/10 border-danger/30 text-danger',
  }[band];
  return (
    <div className={`flex items-baseline gap-1 px-2.5 py-1 rounded-md border ${tint}`}>
      <span className="text-base font-semibold leading-none">{score}</span>
      <span className="text-[10px] opacity-70">/ 100</span>
    </div>
  );
}
